import { SourceMapConsumer } from 'source-map-js';

const OPENOBSERVE_URL = import.meta.env.VITE_OPENOBSERVE_URL;
const OPENOBSERVE_USER = import.meta.env.VITE_OPENOBSERVE_USER;
const OPENOBSERVE_PASS = import.meta.env.VITE_OPENOBSERVE_PASS;
const isLoggingRedirect = import.meta.env.VITE_REDIRECT_LOGS?.toLowerCase() === 'true';
const isGetLocalIp = import.meta.env.VITE_GET_LOCAL_IP?.toLowerCase() === 'true';

let DEVICE_ID: string;
let isOpenObserveAvailable = false;
let isFirstLog = true; //debug remove lance
let isFirstIntermittentError = true; //debug remove lance

const UTILITY_SERVER_HOST = import.meta.env.VITE_UTILITY_SERVER_HOST; 

// Module-scoped variable for caching the SourceMapConsumer instance
let sourceMapper: SourceMapConsumer | null = null;
let sourceMapData: any = null;

export const getExternalIP = async (): Promise<string> => {
  let response = location.hostname;
  
  try {
    const ipRegex = /([0-9]{1,3}\.){3}[0-9]{1,3}/;
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    pc.createDataChannel('');
    await pc.setLocalDescription(await pc.createOffer());

    const candidates: string[] = [];
    await new Promise<void>((resolve) => {
      pc.onicecandidate = (ice) => {
        if (ice?.candidate?.candidate) {
          const match = ipRegex.exec(ice.candidate.candidate);
          if (match) candidates.push(match[0]);
        } else {
          resolve();
        }
      };
    });

    pc.close();

    const ip = candidates.find(ip => !ip.startsWith('192.168.') && !ip.startsWith('10.') && !ip.startsWith('172.'));
    if (ip) return ip;
    if (candidates.length > 0) return candidates[0];
  } catch (error) {
    // Silently handle WebRTC errors
  }

  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipRegex.test(response)) return response;
  
  return response.length > 15 ? response.substring(0, 15) : response;
};

async function testOpenObserveConnection(): Promise<boolean> {
  const controller = new AbortController(); // fetch doesn't timeout so we connect a cancellable hook
  const timeout = setTimeout(() => controller.abort(), 5000); // Set timeout to 5 seconds
  try {
    const currentUrl = new URL(OPENOBSERVE_URL);

    const response = await fetch(currentUrl.protocol + "://" + currentUrl.host, {
      method: "HEAD",
      redirect: 'follow',
      headers: {
        "Authorization": `Basic ${btoa(`${OPENOBSERVE_USER}:${OPENOBSERVE_PASS}`)}`,
      },
      signal: controller.signal, // Attach the AbortController signal
    });
    return response.ok;
  } catch (error){
    if (error instanceof Error && error.name === 'AbortError') {
      alert(`Testing OO server timed out.`); 
      console.error('Testing OO server timed out.');
    } else {
      const currentUrl = new URL(OPENOBSERVE_URL);
      console.warn(`Did not find OpenObserve server at ${currentUrl.protocol + "://" + currentUrl.host}`);   alert(`Did not find OpenObserve server at ${currentUrl.protocol + "://" + currentUrl.host}`); //lance
    }
    return false;
  } finally {
    clearTimeout(timeout); // Clear the timeout
  }
}

const sendToOpenObserve = async (level: string, message: any, stackInfo: string) => { 
  if (!isOpenObserveAvailable || !DEVICE_ID) {
    return; 
  }

  const controller = new AbortController(); // fetch doesn't timeout so we connect a cancellable hook
  const timeout = setTimeout(() => controller.abort(), 5000); // Set timeout to 5 seconds

  try {
    if (isFirstLog) {
      //alert(`Sending first log to ${OPENOBSERVE_URL} to pass ${OPENOBSERVE_PASS}`);
      isFirstLog = false;
    }

    await fetch(OPENOBSERVE_URL, {
      method: "POST",
      redirect: 'follow',
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Basic ${btoa(`${OPENOBSERVE_USER}:${OPENOBSERVE_PASS}`)}`,
      },
      body: JSON.stringify({
        message: typeof message === "string" ? message : JSON.stringify(message),
        level,
        deviceID: DEVICE_ID,
        location: stackInfo
      }),
      signal: controller.signal, // Attach the AbortController signal
    });
  } catch (error) {
    if (isFirstIntermittentError && error instanceof Error && error.name === 'AbortError') {
      isFirstIntermittentError = false; // Only alert once
      alert(`Sending remote logs seems intermittent. Worked at least once so will try next time.`); 
      console.error('Fetch request timed out but trying next time');
    } else {
      console.error('Error sending logs but will try next time:', error);
    }
  } finally {
    clearTimeout(timeout); // Clear the timeout
  }
};

// async function fetchSourceMap(): Promise<any> { // depends on the webservice to get the sourcemap
//   if (sourceMapData) return sourceMapData;

//   const controller = new AbortController(); // fetch doesn't timeout so we connect a cancellable hook
//   const timeout = setTimeout(() => controller.abort(), 5000); // Set timeout to 5 seconds


//   try {
//     const currentUrl = new URL(UTILITY_SERVER_HOST);
//     currentUrl.pathname = "source-map";

//     const response = await fetch(currentUrl.toString(), {
//       redirect: 'follow',

//       signal: controller.signal, // Attach the AbortController signal
//       },
//     );
//     if (!response.ok){
//       console.warn(`Could not send fetch to get soure map`); // alert(`could not send source-map fetch`); //lance
//       return null;
//     } 

//     sourceMapData = await response.json();
//     console.log(`Successfully retrieved sourcemaps`);
//     return sourceMapData;
//   } catch (error) {
//     console.log(`Sourcemap fetch failed: ${error}`); // alert(`source map at ${UTILITY_SERVER_HOST} fetch error: ${error}`); //lance
//     return null;
//   } finally {
//     clearTimeout(timeout); // Clear the timeout
//   }

// }

async function fetchSourceMap(): Promise<any> { //depends on the sourcemap script tag in index.html
  if (sourceMapData) return sourceMapData;
  
  const sourcemapLink = document.getElementById('sourcemap') as HTMLLinkElement; //element created in the index.html file
  if (!sourcemapLink || !sourcemapLink.href) {
    console.warn('Sourcemap script not found');
    return null;
  }

  const controller = new AbortController(); // fetch doesn't timeout so we connect a cancellable hook
  const timeout = setTimeout(() => controller.abort(), 10000); // Set timeout to 10 seconds to allow for a large sourcemap

  try {
    const response = await fetch(sourcemapLink.href, { 
      signal: controller.signal, // Attach the AbortController signal
      },);
      
    if (!response.ok) {
      console.warn('Failed to load sourcemap');
      return null;
    }
    sourceMapData = await response.json();
    console.log('Successfully retrieved sourcemap from script tag', JSON.stringify(sourceMapData).substring(0, 50)); // Log the first 50 characters of the sourcemap data
    return sourceMapData;
  } catch (error) {
    console.log(`Sourcemap fetch failed: ${error}`); // alert(`source map at ${UTILITY_SERVER_HOST} fetch error: ${error}`); //lance
    return null;
  } finally {
    clearTimeout(timeout); // Clear the timeout
  }
}


async function initializeSourceMapConsumer(): Promise<void> {
  if (sourceMapper) return;

  const mapData = await fetchSourceMap();
  if (!mapData) {
    //throw new Error('Failed to fetch sourcemaps'); //let sourceMapper = null; //this is our 'false' state we can check for later
    return;
  }

  try {
    sourceMapper = await new SourceMapConsumer(mapData);
  } catch {
    sourceMapper = null; //this is our 'false' state we can check for later
  }
}

async function mapStackTrace(): Promise<string> {
  const e = new Error();
  if (!e.stack) return "";

  const regex = /.*\/([^?]+).*:(\d+):(\d+)\)?$/;
  const stackLines = e.stack.split('\n').map(line => line.trim());
  const matchingLines = stackLines.filter((line) => regex.test(line));

  if (matchingLines.length < 4) return "";

  const lineOfInterest = matchingLines[3];
  const match = regex.exec(lineOfInterest);
  if (!match) return "";

  const [, file, line, column] = match;

  if (import.meta.env.MODE === 'production') {
    try {
      
      if (!sourceMapper) return `${file}:${line}:${column}`; //sourceMapper == null if it failed to init

      const originalPos = sourceMapper.originalPositionFor({
        line: parseInt(line, 10),
        column: parseInt(column, 10),
      });

      if (originalPos.source) {
        const filename = originalPos.source.split('/').pop() || file;
        return `${filename}:${originalPos.line}:${originalPos.column}`;
      }
    } catch {
      return `${file}:${line}:${column}`;
    }
  }

  return `${file}:${line}:${column}`;
}

async function generateSessionID(): Promise<string> {
 // const stored = localStorage.getItem("sessionID");
  // if (stored && stored.match(/device-\d+\.\d+\.\d+\.\d+-/)) { //reuse if its has the iffy ip address section, else try again
  //   DEVICE_ID = stored;
  //   return stored;
  // }

  let id: string;

  if(isGetLocalIp)
  {
    const controller = new AbortController(); // fetch doesn't timeout so we connect a cancellable hook
    const timeout = setTimeout(() => controller.abort(), 5000); // Set timeout to 5 seconds
  
    try {
      const currentUrl = new URL(UTILITY_SERVER_HOST);
      currentUrl.pathname = "myip";
  
      const response = await fetch(currentUrl.toString(), {
        redirect: 'follow',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal, // Attach the AbortController signal

      });
  
      if (!response.ok) throw new Error('Failed to fetch IP');
  
      const data = await response.json();
      if (!data?.client) throw new Error('Invalid response');
  
      id = data.client;
    } catch (error) {
      console.log(`No local IP from ${UTILITY_SERVER_HOST} =>${error}, fallback to external IP`); // alert(`No local IP from ${UTILITY_SERVER_HOST} =>${error}, fallback to external IP`); //lance
      id = await getExternalIP();
    } finally {
      clearTimeout(timeout); // Clear the timeout
    }
  } else {
    id = await getExternalIP();
  }

  id = id.length > 15 ? id.substring(0, 15) : id;
  const sessionId = `device-${id}-${Math.random().toString(36).substring(2, 9)}`;
  localStorage.setItem("sessionID", sessionId);
  DEVICE_ID = sessionId; //alert(`session id ${sessionId}`); //lance
  return sessionId;
}
// This is for remote debugging when you can't use devtools on a browser, like for mobile devices
// Each log will be called 2x. 1. regular console.xx but file:line info lost, 2. send to OpenObserve with proper file:line from sourcemap
// Since local browser devtools don't have the file:line info you want to disable this if that info is needed
// I didn't want to disable override when in development mode, or in devtools, so I can see logs from remote users, but this could be changed.
const overrideConsole = async () => { 
  if(!isLoggingRedirect) {
    console.log(`Normal logging (not redirecting)`);
    return true;
  } else {
    console.log(`Redirecting logs to ${OPENOBSERVE_URL}`);
  }

  try {
    isOpenObserveAvailable = await testOpenObserveConnection();
    
    if (!isOpenObserveAvailable) {
      console.log(`Unable to reach external log server. Not redirecting`); alert(`Unable to reach external log server. Not redirecting`); // lance
      throw new Error('OpenObserve connection failed');
    }

    if (import.meta.env.MODE === 'production') {
      await initializeSourceMapConsumer();
      if(!sourceMapper) {
        console.log(`Could not read the sourcemaps so file info will be from the bundle and less useful`);// alert(`can't get sourcemap but continue`); // lance
      }
    }
   // alert(`OO verified and sourceMapper ${sourceMapper ? 'loaded': 'not loaded'}`); //lance

    const originalMethods = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console)
    };

    type ConsoleMethod = keyof typeof originalMethods;

    Object.entries(originalMethods).forEach(([methodName, originalFn]) => {
      const method = methodName as ConsoleMethod;
      (console[method] as any) = function(...args: any[]) {
        originalFn(...args);
        
        // Use void to handle the Promise without making the function async
        void (async () => {
          const stackInfo = await mapStackTrace();
          const message = args.map(arg => 
            typeof arg === "string" ? arg : JSON.stringify(arg)
          ).join(" ");
          
          await sendToOpenObserve(
            method,
            message,
            stackInfo ? stackInfo : ""
          );
        })();
      };
    });

    return true;
  } catch (error) {
    return false;
  }
};

export { sendToOpenObserve, overrideConsole, generateSessionID }